import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./AdhocQuery.scss";
import { Form, Button } from "react-bootstrap";
import useAdhocQuery from "../../Hooks/useAdhocQuery";
import Loading from "../../components/LoadingPage/Loading";
import CommonPagination from "../../global/Pagination/CommonPagination";
import { toast } from "react-toastify";
import { format } from "sql-formatter";

type QueryType = "select" | "insert" | "update" | "delete";

const queryTypeOptions: QueryType[] = ["select", "insert", "update", "delete"];

interface AdhocQueryFormValues {
  query: string;
  sortBy: string;
  isDescending: boolean;
  pageNumber: number;
  pageSize: number;
}

const validationSchema = Yup.object({
  query: Yup.string().trim().required("Query text is required"),
});

const AdhocQuery: React.FC = () => {
  const [queryType, setQueryType] = useState<QueryType>("select");
  const [results, setResults] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [paginationPageSize, setPaginationPageSize] = useState<number>(10);
  const [mutationMessage, setMutationMessage] = useState<string>("");
  const [mutationMessageType, setMutationMessageType] = useState<
    "success" | "error"
  >("success");
  const [sortBy, setSortBy] = useState<string>("");
  const [isDescending, setIsDescending] = useState<boolean>(true);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    selectQueryAPI,
    exportQueryAPI,
    insertQueryAPI,
    updateQueryAPI,
    deleteQueryAPI,
    selectQueryLoading,
    exportQueryLoading,
    insertQueryLoading,
    updateQueryLoading,
    deleteQueryLoading,
  } = useAdhocQuery();

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    if (mutationMessage && mutationMessageType === "success") {
      successTimerRef.current = setTimeout(() => {
        setMutationMessage("");
      }, 5000);
    }
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, [mutationMessage, mutationMessageType]);

  const formik = useFormik<AdhocQueryFormValues>({
    initialValues: {
      query: "",
      sortBy: "",
      isDescending: true,
      pageNumber: 1,
      pageSize: 10,
    },
    validationSchema,
    onSubmit: async (values) => {
      const apiPool: Record<QueryType, (data: any) => Promise<any>> = {
        select: selectQueryAPI,
        insert: insertQueryAPI,
        update: updateQueryAPI,
        delete: deleteQueryAPI,
      };

      try {
        // Clean and format the query before calling the API
        const cleanedQuery = cleanQueryInput(values.query);
        let formattedQuery = cleanedQuery;
        try {
          formattedQuery = format(cleanedQuery, {
            language: "postgresql",
            keywordCase: "upper",
          });
        } catch {
          try {
            formattedQuery = format(cleanedQuery, { keywordCase: "upper" });
          } catch {
            // Formatting failed - use cleaned query as-is
          }
        }
        formik.setFieldValue("query", formattedQuery);

        const payload = {
          query: formattedQuery,
          sortBy: sortBy,
          isDescending: isDescending,
          pageNumber: values.pageNumber,
          pageSize: values.pageSize,
        };
        const res = await apiPool[queryType](payload);

        if (queryType === "select") {
          setMutationMessage("");
          if (res.result && res.result.length > 0) {
            setResults(res.result || []);

            // Extract total records from message field
            let totalRecordsCount = res.result.length;
            if (res.message) {
              const match = res.message.match(/Total Records:\s*(\d+)/);
              if (match) {
                totalRecordsCount = parseInt(match[1], 10);
              }
            }
            setTotalRecords(totalRecordsCount);

            // Extract columns from the first object's keys
            const extractedColumns = Object.keys(res.result[0]);
            setColumns(extractedColumns);
            setPaginationPageSize(values.pageSize);
          } else {
            setResults([]);
            setColumns([]);
            setTotalRecords(0);
            toast.info("No records found.");
          }
        } else {
          // insert / update / delete
          setResults([]);
          setColumns([]);
          setTotalRecords(0);
          const message =
            res?.message || `${queryType} query executed successfully.`;
          setMutationMessageType("success");
          setMutationMessage(message);
        }
      } catch (err: any) {
        console.error("Error while executing query:", err);
        const errorMsg =
          err?.message || "An error occurred while executing the query.";
        setMutationMessageType("error");
        setMutationMessage(errorMsg);
      }
    },
  });

  const handleSort = async (column: string) => {
    const newIsDescending = sortBy === column ? !isDescending : false;
    setSortBy(column);
    setIsDescending(newIsDescending);

    try {
      const payload = {
        ...formik.values,
        sortBy: column,
        isDescending: newIsDescending,
        pageNumber: 1,
      };
      formik.setFieldValue("pageNumber", 1);
      const res = await selectQueryAPI(payload);
      if (res.result && res.result.length > 0) {
        setResults(res.result || []);
      }
    } catch (err) {
      console.error("Error while sorting:", err);
    }
  };

  const handlePageChange = async (page: number, _perPage: number) => {
    if (!formik.values) return;
    formik.setFieldValue("pageNumber", page);

    try {
      const payload = {
        ...formik.values,
        sortBy,
        isDescending,
        pageNumber: page,
      };
      const res = await selectQueryAPI(payload);
      if (res.result && res.result.length > 0) {
        setResults(res.result || []);
      }
    } catch (err) {
      console.error("Error while fetching page:", err);
    }
  };

  const handleExport = async () => {
    try {
      const cleanedQuery = cleanQueryInput(formik.values.query);
      const payload = {
        query: cleanedQuery,
        sortBy: sortBy,
        isDescending: isDescending,
        pageNumber: 1,
        pageSize: totalRecords || 1000,
      };
      const blob: any = await exportQueryAPI(payload);
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "adhoc-query-export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("File downloaded successfully");
      }
    } catch (err: any) {
      console.error("Error while exporting:", err);
      toast.error(err?.message || "An error occurred while exporting.");
    }
  };

  const cleanQueryInput = (input: string): string => {
    let query = input.trim();

    // Try JSON.parse first for properly escaped JSON strings
    if (query.startsWith('"')) {
      try {
        const parsed = JSON.parse(query);
        if (typeof parsed === "string") return parsed.trim();
      } catch {
        // Not valid JSON - fall through to manual cleanup
      }
    }

    // Manual cleanup: unescape \" -> " then strip outer quotes
    query = query.replace(/\\"/g, '"');
    if (query.startsWith('"') && query.endsWith('"')) {
      query = query.slice(1, -1);
    }
    return query.trim();
  };

  const isLoading =
    selectQueryLoading ||
    exportQueryLoading ||
    insertQueryLoading ||
    updateQueryLoading ||
    deleteQueryLoading;

  return (
    <div className="adhoc-query-wrapper">
      {isLoading && <Loading />}
      <div className="adhoc-query-header">
        <h5 className="mb-0">Adhoc Query</h5>
      </div>
      <div className="adhoc-query-body">
        <Form onSubmit={formik.handleSubmit}>
          <div className="adhoc-query-controls">
            <Form.Group className="adhoc-query-type">
              <Form.Label className="adhoc-label">Query Type:</Form.Label>
              {queryTypeOptions.map((type) => (
                <Form.Check
                  key={type}
                  inline
                  type="radio"
                  id={`queryType-${type}`}
                  name="queryType"
                  label={type}
                  value={type}
                  checked={queryType === type}
                  onChange={() => {
                    setQueryType(type);
                    formik.setFieldValue("query", "");
                    formik.setTouched({ query: false });
                    setResults([]);
                    setColumns([]);
                    setTotalRecords(0);
                    setMutationMessage("");
                    setSortBy("");
                    setIsDescending(true);
                  }}
                  className="adhoc-radio-label"
                />
              ))}
            </Form.Group>

            {queryType === "select" && (
              <Form.Group className="adhoc-page-size">
                <Form.Label htmlFor="pageSize" className="adhoc-label">
                  Page Size:
                </Form.Label>
                <Form.Control
                  id="pageSize"
                  name="pageSize"
                  type="number"
                  className="adhoc-page-size-input"
                  value={formik.values.pageSize}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue("pageNumber", 1);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.pageSize && !!formik.errors.pageSize
                  }
                />
              </Form.Group>
            )}

            <div className="adhoc-actions">
              <Button type="submit" variant="primary">
                {queryType === "select" ? "View" : "Execute"}
              </Button>
              {queryType === "select" && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="adhoc-export-btn"
                  onClick={handleExport}
                  disabled={results.length === 0}
                >
                  Export
                </Button>
              )}
            </div>
          </div>

          <Form.Group className="adhoc-query-control">
            <Form.Control
              as="textarea"
              name="query"
              className="adhoc-textarea"
              value={formik.values.query}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.query && !!formik.errors.query}
              placeholder="Enter your SQL query here..."
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.query}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>

        {results.length > 0 && queryType === "select" && (
          <div className="adhoc-query-results">
            <div className="listing-table-wrapper">
              <table className="table table-hover">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="sortable-header"
                        onClick={() => handleSort(col)}
                      >
                        {col}
                        {sortBy === col && (
                          <span className="sort-icon">
                            {isDescending ? " \u25BC" : " \u25B2"}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((row, idx) => (
                    <tr key={idx}>
                      {columns.map((col) => (
                        <td key={col}>{row[col] ?? "-"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CommonPagination
              totalRecords={totalRecords}
              onChange={handlePageChange}
              perPage={paginationPageSize}
            />
          </div>
        )}

        {mutationMessage && (
          <div className="adhoc-mutation-result">
            <div
              className={`alert ${
                mutationMessageType === "error"
                  ? "alert-danger"
                  : "alert-success"
              }`}
            >
              {mutationMessage}
              {mutationMessageType === "error" && (
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setMutationMessage("")}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdhocQuery;
